import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/useToast';
import { parseItemBarcode } from '@/lib/barcode';
import { Camera, X, Barcode, Package, MapPin, Hash, AlertCircle } from 'lucide-react';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onScan: (result: BarcodeResult) => void;
  title?: string;
  description?: string;
}

export interface BarcodeResult {
  barcode: string;
  type: 'item' | 'location' | 'batch' | 'po' | 'rma' | 'unknown';
  parsed: {
    itemCode?: string;
    batchNumber?: string;
    locationCode?: string;
    poNumber?: string;
    rmaNumber?: string;
  };
}

interface CameraError {
  message: string;
  type: 'permission' | 'not-found' | 'unknown';
}

export function BarcodeScanner({
  open,
  onClose,
  onScan,
  title = 'Scan Barcode',
  description = 'Position the barcode within the camera frame',
}: BarcodeScannerProps) {
  const { toast } = useToast();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState<CameraError | null>(null);
  const [manualInput, setManualInput] = useState('');
  const [lastScanned, setLastScanned] = useState<string | null>(null);

  const parseBarcode = useCallback((barcode: string): BarcodeResult => {
    const parsed = parseItemBarcode(barcode);
    
    let type: BarcodeResult['type'] = 'unknown';
    
    if (barcode.startsWith('PO-')) {
      type = 'po';
    } else if (barcode.startsWith('RMA-')) {
      type = 'rma';
    } else if (barcode.startsWith('LOC-')) {
      type = 'location';
    } else if (parsed.batchNumber) {
      type = 'batch';
    } else if (parsed.itemCode) {
      type = 'item';
    }

    return {
      barcode,
      type,
      parsed: {
        itemCode: parsed.itemCode,
        batchNumber: parsed.batchNumber,
        locationCode: parsed.locationCode,
        poNumber: barcode.startsWith('PO-') ? barcode.substring(3) : undefined,
        rmaNumber: barcode.startsWith('RMA-') ? barcode.substring(4) : undefined,
      },
    };
  }, []);

  const handleScan = useCallback((barcode: string) => {
    if (barcode === lastScanned) return;
    
    setLastScanned(barcode);
    
    try {
      const result = parseBarcode(barcode);
      onScan(result);
      onClose();
      
      toast({
        title: 'Barcode Scanned',
        description: `${result.type.toUpperCase()}: ${barcode}`,
      });
      
      // Reset after delay to allow scanning same code again
      setTimeout(() => setLastScanned(null), 2000);
    } catch (error) {
      toast({
        title: 'Scan Error',
        description: 'Failed to process barcode',
        variant: 'destructive',
      });
    }
  }, [lastScanned, onScan, onClose, toast, parseBarcode]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsScanning(true);
        setCameraError(null);
      }
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        setCameraError({
          message: 'Camera access denied. Please enable camera permissions.',
          type: 'permission',
        });
      } else if (error.name === 'NotFoundError') {
        setCameraError({
          message: 'No camera found on this device.',
          type: 'not-found',
        });
      } else {
        setCameraError({
          message: 'Failed to access camera.',
          type: 'unknown',
        });
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setIsScanning(false);
  }, []);

  const simulateScan = useCallback(() => {
    // For demo purposes, simulate a barcode scan
    const mockBarcodes = [
      'ITEM-001-B12345-LWH001',
      'PO-2026-0001',
      'RMA-2026-00000001',
      'LOC-WH-001',
      'MED-001-BATCH-2026',
    ];
    const randomBarcode = mockBarcodes[Math.floor(Math.random() * mockBarcodes.length)];
    handleScan(randomBarcode);
  }, [handleScan]);

  useEffect(() => {
    if (open) {
      startCamera();
    } else {
      stopCamera();
    }
    
    return () => {
      stopCamera();
    };
  }, [open, startCamera, stopCamera]);

  const handleManualSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualInput.trim()) {
      handleScan(manualInput.trim());
      setManualInput('');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Barcode className="h-5 w-5" />
            {title}
          </DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Camera view */}
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              muted
            />
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Scanning overlay */}
            {isScanning && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-3/4 h-1/3 border-2 border-primary-400 rounded-lg relative">
                  <div className="absolute inset-0 animate-pulse bg-primary-400/20 rounded-lg" />
                  <div className="absolute top-0 left-0 w-full h-1 bg-primary-400 animate-scan" />
                </div>
              </div>
            )}
            
            {/* Camera error */}
            {cameraError && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
                <AlertCircle className="h-12 w-12 mb-4 text-error" />
                <p className="text-center text-sm">{cameraError.message}</p>
              </div>
            )}
            
            {/* No camera message */}
            {!cameraError && !isScanning && (
              <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-4">
                <Camera className="h-12 w-12 mb-4 text-gray-400" />
                <p className="text-center text-sm">Starting camera...</p>
              </div>
            )}
          </div>

          {/* Quick actions */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="flex-1"
              onClick={simulateScan}
              disabled={isScanning}
            >
              <Barcode className="h-4 w-4 mr-2" />
              Simulate Scan
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={isScanning ? stopCamera : startCamera}
            >
              {isScanning ? (
                <>
                  <X className="h-4 w-4 mr-2" />
                  Stop Camera
                </>
              ) : (
                <>
                  <Camera className="h-4 w-4 mr-2" />
                  Start Camera
                </>
              )}
            </Button>
          </div>

          {/* Manual input */}
          <form onSubmit={handleManualSubmit} className="space-y-2">
            <label className="text-sm font-medium">Or enter barcode manually:</label>
            <div className="flex gap-2">
              <Input
                value={manualInput}
                onChange={(e) => setManualInput(e.target.value)}
                placeholder="Enter barcode number..."
                className="flex-1"
              />
              <Button type="submit" disabled={!manualInput.trim()}>
                Submit
              </Button>
            </div>
          </form>

          {/* Format guide */}
          <Card className="bg-muted/50">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm">Supported Barcode Formats</CardTitle>
            </CardHeader>
            <CardContent className="text-xs space-y-1">
              <div className="flex items-center gap-2">
                <Package className="h-3 w-3" />
                <span>Item: <code className="bg-muted px-1 rounded">ITEM-CODE-BATCH-LOCATION</code></span>
              </div>
              <div className="flex items-center gap-2">
                <Hash className="h-3 w-3" />
                <span>PO: <code className="bg-muted px-1 rounded">PO-2026-0001</code></span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-3 w-3" />
                <span>Location: <code className="bg-muted px-1 rounded">LOC-WH-001</code></span>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default BarcodeScanner;
